import React, { Component } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';


import {
  UniTable,
  UniConditionalRender,
  UniIcon,
  IUniTable_Column,
  IPaginatedResponseC,
  IPaginationQueryBuilderParamsC,
  IReaderC,
  ReaderC,
  OrganizationC,
  IUniConfirm_Config,
  IUniTable_UpdatePaginationSummary,
  IUniTable_Filter,
  IUniTable_Sort,
  IUniTable_Action,
  IUniTable_PaginationSummary,
  EOperationCodesC,
  UniTag,
  IUniTable_Props,
  EUniTable_FooterStyle,
  IRouteMatchParamsHOCProps,
  withRouteMatchParams
} from '@unikey/unikey-commons/release/comm';

import {
  ReaderEnrollContainer,
  buildTableUpdateFunc, TTableUpdateFunc,
  getTableSortDirection, getTableParamsFromUpdate,
  PartnerCustomizations, IPartnerCustomizations,
  attemptRetrieveOrgDetails,
  updateReaderListQueryParams,
  attemptRetrieveReadersList,
  updateReadersTableMeta,
  attemptDeleteReader,
  toggleEnrollReadersModal,
  openConfirmModal,
  closeConfirmModal,
  canI,
  navConfig, ENavPages,
  IToggleEnrollReadersModalActionParams,
  addPendingRedirect,
  IPendingRedirectActionParams
} from '../internal';

interface IProps extends WrappedComponentProps, IPartnerCustomizations, IRouteMatchParamsHOCProps {
  readerData: IPaginatedResponseC<IReaderC, ReaderC>,
  readerDataModels: ReaderC[],
  readerListQueryParams: IPaginationQueryBuilderParamsC,
  appliedFilters: IUniTable_Filter[],
  appliedSorts: IUniTable_Sort[],
  paginationSummary: IUniTable_PaginationSummary,
  listLoading: boolean,
  hideEnrollment2: boolean,
  isMini: boolean,

  orgId: string,
  orgDetails: OrganizationC,
  permissionToDeleteReader: boolean,
  permissionToViewIndividualReader: boolean,

  getOrgDetails(orgId: string): Promise<void>,
  updateQueryParams?(params: IPaginationQueryBuilderParamsC): void,
  updateTableMeta(metaSummary: IUniTable_UpdatePaginationSummary): void,
  getOrgReaders(orgId: string): Promise<void>,
  deleteReader(readerId: string): Promise<void>,
  openDeleteReaderConfirmDialog(dialogConfig: IUniConfirm_Config): void,
  closeConfirmModal(): void,
  toggleEnrollModal(params: IToggleEnrollReadersModalActionParams): void,
  addPendingRedirect(redirOpts: IPendingRedirectActionParams): void,
}

class ReaderListContainer extends Component<IProps> {
  _updateTable: TTableUpdateFunc; 

  constructor(props: IProps) {
    super(props);

    this._updateTable = buildTableUpdateFunc(
      props.getOrgReaders.bind(this, this.props.orgId),
      props.updateTableMeta,
      props.updateQueryParams
    )
  }

  componentDidMount() {
    this.props.getOrgDetails(this.props.orgId);
    this.props.getOrgReaders(this.props.orgId);
  }

  _deleteReaderAndRefreshList = (readerId: string) => {
    this.props.deleteReader(readerId).then(this._refreshReadersList);
  }

  _refreshReadersList = () => {
    this.props.getOrgReaders(this.props.orgId);
  }

  _openEnrollDialog = () => {
    this.props.toggleEnrollModal({
      orgId: this.props.orgId,
      afterAction: () => this._refreshReadersList()
    });
  }

  _buildColumnsAndActions() {
    const columns = new Map<string, IUniTable_Column>()
      .set('name', {
        nameKey: 'name',
        isSortable: true,
        sortFields: ['name'],
        isPrimaryFilter: true,
        size: 3
      })
      .set('serialNumber', {
        nameKey: 'serialNumber',
        isSortable: false,
        size: 2,
        evalItalicized: (rowItem: ReaderC) => !rowItem.serialNumber
      })
      .set('firmwareVersion', {
        nameKey: 'version',
        isSortable: false,
        size: 2,
        type: 'taginverted'
      })
      .set('upgradeAvailable', {
        nameKey: 'status',
        isSortable: false,
        size: 1,
        template: (rowItem: ReaderC) => (
          <UniIcon
            size="sm"
            tooltipPosition="top"
            tooltipTextKeys={rowItem.upgradeAvailable ? ['firmwareUpgradeAvailable'] : undefined}
            tooltipBoundary="scrollParent"
            name={rowItem.upgradeAvailable ? 'warning' : 'check'} />
        )
      })
      .set('availableUpgradeVersion', {
        nameKey: 'upgradeVersion',
        isSortable: false,
        size: 2
      })
      .set('actions', {
        nameKey: 'actions',
        isSortable: false,
        isFilterable: false,
        collapsed: true,
        size: 2
      });

    const actions = new Map<string, IUniTable_Action>()
      .set('view', {
        nameKey: 'view',
        icon: 'removeRedEye',
        isDefaultAction: true,
        func: (rowItem: ReaderC) => this.props.addPendingRedirect({ 
          pathname: navConfig.get(ENavPages.readerDetails)!.linkTo([this.props.orgId, rowItem.id])!
        }),
        evalDisabled: (rowItem: ReaderC) => false,
        evalVisible: (rowItem: ReaderC) => this.props.permissionToViewIndividualReader
      })
      .set('removeReader', {
        nameKey: 'removeReader',
        icon: 'delete',
        func: (rowItem: ReaderC) => this.props.openDeleteReaderConfirmDialog({
          titleKey: 'removeReader',
          messageKeys: ['youAreAboutToDeleteThisReader', rowItem.name],
          confirmTextKey: 'delete',
          cancelHandler: this.props.closeConfirmModal,
          confirmHandler: () => {
            this._deleteReaderAndRefreshList(rowItem.id)
            this.props.closeConfirmModal()
          }
        }),
        theme: 'fail',
        evalDisabled: (rowItem: ReaderC) => false,
        evalVisible: (rowItem: ReaderC) => this.props.permissionToDeleteReader
      });

    return { columns, actions };
  }

  
  _buildMiniColumns() {
    const columns = new Map<string, IUniTable_Column>()
      .set('name', {
        nameKey: 'name',
        isSortable: true,
        sortFields: ['name'],
        isPrimaryFilter: true,
        size: 14
      })
      .set('firmwareVersion', {
        nameKey: 'version',
        isSortable: false,
        size: 5,
        type: 'taginverted'
      })
      .set('upgradeAvailable', {
        nameKey: 'status',
        isSortable: false,
        size: 5,
        template: (rowItem: ReaderC) => (
          <UniIcon
            size="sm"
            tooltipPosition="top"
            tooltipTextKeys={rowItem.upgradeAvailable ? ['firmwareUpgradeAvailable'] : undefined}
            tooltipBoundary="scrollParent"
            name={rowItem.upgradeAvailable ? 'warning' : 'check'} />
        )
      });

    const actions = new Map<string, IUniTable_Action>()
      .set('view', {
        nameKey: 'view',
        icon: 'removeRedEye',
        isDefaultAction: true,
        func: (rowItem: ReaderC) => this.props.addPendingRedirect({ 
          pathname: navConfig.get(ENavPages.readerDetails)!.linkTo([this.props.orgId, rowItem.id])!
        }),
        evalDisabled: (rowItem: ReaderC) => false,
        evalVisible: (rowItem: ReaderC) => this.props.permissionToViewIndividualReader
      });

    return { miniColumns: columns, miniActions: actions };
  }


  render() {
    if (this.props.render) {
      return this.props.render();
    }
    const { columns, actions } = this._buildColumnsAndActions();
    
    // non mini props 
    var sizeDependentTableProps: Partial<IUniTable_Props> = {
      preTitleDisplay: (
        <UniTag textKey={this.props.orgDetails.name ?? ''} icon="business" inverted allowWrap useDefaultColor withNeighborTag />
      )
    };
    // overwrite dynamic props if mini desired
    if (this.props.isMini) {
      const { miniColumns, miniActions } = this._buildMiniColumns();
      sizeDependentTableProps = {
        searchable: false,
        createButtonTextHidden: true,
        handleCreateClick: undefined,
        preTitleDisplay: undefined,
        data: this.props.readerDataModels?.slice(0, 5),
        columnConfig: miniColumns,
        actionsConfig: miniActions,
        preventDynamicColumnHiding: true,
        loadMoreTextKey: 'seeAllOrgReaders',
        loadMoreAction: () => this.props.addPendingRedirect({ pathname: navConfig.get(ENavPages.readers)!.linkTo([this.props.orgId ?? '']) }),
        footerStyle: EUniTable_FooterStyle.loadMoreWithSummary,
      };
    }

    return (
      <>
        <section className="readerList-container">
          <UniTable
            searchable={true}
            titleKey="readersList"
            createButtonTextKey={!this.props.hideEnrollment2 ? 'enrollReaders' : undefined}
            handleCreateClick={!this.props.hideEnrollment2 ? this._openEnrollDialog : undefined}
            handleUpdate={this._updateTable}
            data={this.props.readerDataModels}
            columnConfig={columns}
            actionsConfig={actions}
            activeSorts={this.props.appliedSorts}
            activeFilters={this.props.appliedFilters}
            paginationSummary={this.props.paginationSummary}
            showLoader={this.props.listLoading} 
            {...sizeDependentTableProps} />
        </section>
      </>
    )
  }
}

function mapStateToProps(state: any, ownProps: IProps) {
  const orgId = ownProps.orgId ?? ownProps.match?.params?.organizationId;
  return {
    readerData: state.readers.data,
    readerDataModels: state.readers.data?.models ?? [],
    readerListQueryParams: state.readers.queryParams,
    appliedFilters: state.readers.tableFilters,
    appliedSorts: state.readers.tableSorts,
    paginationSummary: state.readers.paginationSummary,
    listLoading: state.readers.loading,
    orgDetails: state.orgDetails.origOrg,
    orgId,
    // permissions
    permissionToDeleteReader: canI(EOperationCodesC.DeleteReader, state.dealer.dealerData.id, orgId),
    permissionToViewIndividualReader: canI(EOperationCodesC.ReadReader, state.dealer.dealerData.id, orgId)
  }
}

const mapDispatchToProps = (dispatch: any) => bindActionCreators({
  toggleEnrollModal: toggleEnrollReadersModal,
  getOrgDetails: attemptRetrieveOrgDetails,
  getOrgReaders: attemptRetrieveReadersList,
  updateQueryParams: updateReaderListQueryParams,
  updateTableMeta: updateReadersTableMeta,

  deleteReader: attemptDeleteReader,
  openDeleteReaderConfirmDialog: openConfirmModal,
  closeConfirmModal,
  addPendingRedirect,
}, dispatch)

export default PartnerCustomizations(
  withRouteMatchParams(
    connect(mapStateToProps, mapDispatchToProps)(injectIntl(ReaderListContainer))
  ), { componentName: 'ReaderList' })
